// Deterministic "how to type" instructions per OS. Inputs are derived from
// the codepoint(s); the small curated tables below cover symbols that have
// well-known dedicated keystrokes (Mac Option combos, Linux Compose). For
// everything else we fall back to the universal hex-input methods that work
// for any Unicode character.
import { codePoints } from "./symbol-formats";

export interface TypingInstruction {
  os: "Windows" | "macOS" | "Linux";
  steps: string[];
  note?: string;
}

// Mac Option / Option+Shift keystrokes for symbols accessible from the
// default US keyboard layout. Anything not in this map gets the universal
// "Unicode Hex Input" instruction.
const MAC_OPTION: Record<number, string> = {
  0x00a9: "Option + G", // ©
  0x00ae: "Option + R", // ®
  0x2122: "Option + 2", // ™
  0x00b0: "Option + Shift + 8", // °
  0x00a2: "Option + 4", // ¢
  0x00a3: "Option + 3", // £
  0x00a5: "Option + Y", // ¥
  0x20ac: "Option + Shift + 2", // €
  0x00b1: "Option + Shift + =", // ±
  0x00f7: "Option + /", // ÷
  0x2260: "Option + =", // ≠
  0x2264: "Option + ,", // ≤
  0x2265: "Option + .", // ≥
  0x221a: "Option + V", // √
  0x221e: "Option + 5", // ∞
  0x2202: "Option + D", // ∂
  0x222b: "Option + B", // ∫
  0x03c0: "Option + P", // π
  0x03a9: "Option + Z", // Ω
  0x00b5: "Option + M", // µ
  0x2026: "Option + ;", // …
  0x2013: "Option + -", // –
  0x2014: "Option + Shift + -", // —
  0x201c: "Option + [", // “
  0x201d: "Option + Shift + [", // ”
  0x2018: "Option + ]", // ‘
  0x2019: "Option + Shift + ]", // ’
  0x00a1: "Option + 1", // ¡
  0x00bf: "Option + Shift + ?", // ¿
  0x00ab: "Option + \\", // «
  0x00bb: "Option + Shift + \\", // »
  0x2022: "Option + 8", // •
  0x00b6: "Option + 7", // ¶
  0x00a7: "Option + 6", // §
  0x2020: "Option + T", // †
  0x2021: "Option + Shift + 7", // ‡
};

// Linux Compose-key sequences for common symbols. Compose-key is opt-in,
// so we always show Ctrl+Shift+U as the universal path and add Compose when
// it's both well-known and short.
const LINUX_COMPOSE: Record<number, string> = {
  0x00a9: "Compose + O + C", // ©
  0x00ae: "Compose + O + R", // ®
  0x2122: "Compose + T + M", // ™
  0x00b0: "Compose + O + O", // °
  0x00a3: "Compose + L + -", // £
  0x20ac: "Compose + = + E", // €
  0x00a5: "Compose + Y + =", // ¥
  0x00bd: "Compose + 1 + 2", // ½
  0x00bc: "Compose + 1 + 4", // ¼
  0x00be: "Compose + 3 + 4", // ¾
  0x00b1: "Compose + + + -", // ±
  0x00f7: "Compose + : + -", // ÷
  0x00d7: "Compose + x + x", // ×
  0x2026: "Compose + . + . + .", // …
  0x2014: "Compose + - + - + -", // —
  0x2013: "Compose + - + - + .", // –
  0x201c: 'Compose + < + "', // “
  0x201d: 'Compose + > + "', // ”
  0x00bf: "Compose + ? + ?", // ¿
  0x00a1: "Compose + ! + !", // ¡
};

const padHex = (cp: number) => cp.toString(16).toUpperCase().padStart(4, "0");

function joinHex(cps: number[]): string {
  return cps.map(padHex).join(" ");
}

export function windowsInstruction(s: string): TypingInstruction {
  const cps = codePoints(s);
  const hex = joinHex(cps);
  // Single BMP codepoint ≤ 255 → classic Alt-numeric-pad code.
  if (cps.length === 1 && cps[0] <= 255) {
    const dec = cps[0].toString().padStart(4, "0");
    return {
      os: "Windows",
      steps: [
        `Hold Alt and type ${dec} on the numeric keypad, then release Alt.`,
        `Or, in Word: type ${hex} then press Alt + X.`,
      ],
      note: "Numeric keypad required (laptops: enable Num Lock or use Fn).",
    };
  }
  // BMP > 255 → Alt+X in Word works; Win+. emoji picker is most reliable.
  if (cps.length === 1 && cps[0] <= 0xffff) {
    return {
      os: "Windows",
      steps: [
        `In Word / Outlook: type ${hex} then press Alt + X.`,
        `Anywhere: press Win + . to open the emoji & symbol picker, then search.`,
      ],
    };
  }
  // Astral / multi-codepoint.
  return {
    os: "Windows",
    steps: [
      `Press Win + . (period) to open the emoji & symbol picker.`,
      `Or in Word: type ${hex.split(" ")[0]} then Alt + X for the first codepoint.`,
    ],
    note: "Multi-codepoint sequence — emoji picker is the reliable option.",
  };
}

export function macInstruction(s: string): TypingInstruction {
  const cps = codePoints(s);
  if (cps.length === 1 && MAC_OPTION[cps[0]]) {
    return {
      os: "macOS",
      steps: [
        `Press ${MAC_OPTION[cps[0]]} on a US keyboard.`,
        `Or: Control + Command + Space opens the Character Viewer.`,
      ],
    };
  }
  if (cps.length === 1 && cps[0] <= 0xffff) {
    const hex = padHex(cps[0]);
    return {
      os: "macOS",
      steps: [
        `Enable “Unicode Hex Input” in System Settings → Keyboard, then hold Option and type ${hex}.`,
        `Or: Control + Command + Space opens the Character Viewer — search by name.`,
      ],
    };
  }
  return {
    os: "macOS",
    steps: [
      `Press Control + Command + Space to open the Character Viewer.`,
      `Search by name or paste the symbol — easiest path for multi-codepoint sequences.`,
    ],
  };
}

export function linuxInstruction(s: string): TypingInstruction {
  const cps = codePoints(s);
  const hex = joinHex(cps);
  if (cps.length === 1) {
    const single = padHex(cps[0]).replace(/^0+/, "") || "0";
    const compose = LINUX_COMPOSE[cps[0]];
    const steps = [
      `Press Ctrl + Shift + U, type ${single}, then press Enter or Space.`,
    ];
    if (compose) steps.push(`Or with a Compose key configured: ${compose}.`);
    return { os: "Linux", steps };
  }
  return {
    os: "Linux",
    steps: cps.map(
      (cp) =>
        `Ctrl + Shift + U, type ${padHex(cp).replace(/^0+/, "") || "0"}, then Space — repeat for each codepoint (${hex}).`,
    ),
    note: "Multi-codepoint sequence — input each component, the renderer joins them.",
  };
}

export function allTypingInstructions(s: string): TypingInstruction[] {
  return [windowsInstruction(s), macInstruction(s), linuxInstruction(s)];
}
